// frontend/src/utils/analytics.js
// Google Analytics 4 tracking utilities

/**
 * Initialize Google Analytics
 * Call this once in your App.js
 */
export const initGA = (measurementId) => {
  if (typeof window === 'undefined') return;

  // Check if gtag is already loaded
  if (window.gtag) {
    console.log('✅ Google Analytics already initialized');
    return;
  }

  // Load gtag.js
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false, // We'll send manually for SPA routing
  });

  console.log('✅ Google Analytics initialized:', measurementId);
};

/**
 * Track page views
 * Call this on route changes in React
 */
export const trackPageView = (path, title) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href
  });

  console.log('📊 Page view tracked:', path);
};

/**
 * Track custom events
 * Examples:
 * - trackEvent('article_view', { article_id: '123', category: 'crypto' })
 * - trackEvent('reward_earned', { amount: 0.01 })
 * - trackEvent('sign_up', { method: 'email' })
 */
export const trackEvent = (eventName, parameters = {}) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', eventName, parameters);

  console.log('📊 Event tracked:', eventName, parameters);
};

/**
 * Track user properties
 * Call when user logs in or changes properties
 */
export const setUserProperties = (properties) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('set', 'user_properties', properties);

  console.log('👤 User properties set:', properties);
};

/**
 * Track user ID (for logged-in users)
 * Call after user logs in
 */
export const setUserId = (userId) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('set', { user_id: userId });

  console.log('🆔 User ID set:', userId);
};

// Common event tracking functions

export const trackArticleView = (article) => {
  trackEvent('article_view', {
    article_id: article.id,
    article_title: article.title,
    category: article.category,
    is_breaking: article.is_breaking || false
  });
};

export const trackSearch = (query, resultsCount) => {
  trackEvent('search', {
    search_term: query,
    results_count: resultsCount
  });
};

export const trackSignUp = (method, hasWallet) => {
  trackEvent('sign_up', {
    method: method,
    has_wallet: hasWallet
  });
};

export const trackLogin = (method) => {
  trackEvent('login', {
    method: method
  });
};

export const trackRewardEarned = (amount, newBalance) => {
  trackEvent('reward_earned', {
    amount: amount,
    new_balance: newBalance,
    currency: 'DOGE'
  });
};

export const trackWalletAdded = (walletRank) => {
  trackEvent('wallet_added', {
    wallet_rank: walletRank
  });
};

export const trackNewsletterSubscribe = () => {
  trackEvent('newsletter_subscribe');
};

export const trackShare = (article, platform) => {
  trackEvent('share', {
    content_type: 'article',
    item_id: article.id,
    method: platform
  });
};

export default {
  initGA,
  trackPageView,
  trackEvent,
  setUserProperties,
  setUserId,
  trackArticleView,
  trackSearch,
  trackSignUp,
  trackLogin,
  trackRewardEarned,
  trackWalletAdded,
  trackNewsletterSubscribe,
  trackShare
};